<script>
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import { storeToRefs } from 'pinia';

  import { ROUTE_NAMES } from '@/constants';
  import { SPLIT_NAMES } from '@/constants/featureFlags';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { track } from '@/track';
  import getAutoPauseCutoffDays from '@/utils/getAutoPauseCutoffDays';

  export default {
    name: 'PastDueAlert',

    components: {
      Button,
      Icon,
    },

    emits: ['navigate'],

    computed: {
      overDueCount() {
        return this.activeCompany.billing.unpaidOverdueCount;
      },

      daysToBePaused() {
        return (
          this.getAutoPauseCutoffDays(this.isSplitEnabled('fire_pod__mp_auto_pausing_update')) -
          this.activeCompany.billing.pausingDaysOutstanding
        );
      },

      isToBePaused() {
        const isLockdownEnabled = this.isSplitEnabled(SPLIT_NAMES.DELINQUENT_LOCKDOWN);

        return (
          this.isSplitEnabled(SPLIT_NAMES.AUTO_PAUSING) &&
          !this.isDelinquent(isLockdownEnabled) &&
          this.daysToBePaused > 0 &&
          this.activeCompany.billing.pausingDaysOutstanding > 0
        );
      },
    },

    created() {
      this.ROUTE_NAMES = ROUTE_NAMES;
    },

    setup() {
      const companyStore = useCompanyStore();
      const { activeCompany } = storeToRefs(companyStore);

      const userStore = useUserStore();
      const { getIsSplitEnabled, user, userActiveCompanyPermissions } = storeToRefs(userStore);

      return {
        activeCompany,
        isDelinquent: companyStore.isDelinquent,
        isSplitEnabled: getIsSplitEnabled,
        permissions: userActiveCompanyPermissions,
        user,
        getAutoPauseCutoffDays,
      };
    },

    methods: {
      onClickViewBilling() {
        track('View Billing Info Clicked', {
          source: 'Alert in side nav for invoices past due',
          user_id: this.user.id,
          username: this.user.username,
          user_email: this.user.email,
          company_id: this.activeCompany.id,
          company_slug: this.activeCompany.slug,
          is_admin: !!this.userStaffPermissions?.isAdmin,
        });

        this.$emit('navigate');
      },
    },
  };
</script>

<template>
  <div class="ll-pa-2 border rounded" :class="$style['past-due']">
    <div class="flex align-middle">
      <div class="text-red ll-pr-2">
        <Icon name="circle-warning" />
      </div>
      <div class="text-white" :class="$style['past-due-text']">
        <span>
          <div class="tw-mb-2">
            {{
              overDueCount > 1
                ? $t('layout.companyStatus.notifyInvoicesPastDue.line1Plural', { num: overDueCount })
                : $t('layout.companyStatus.notifyInvoicesPastDue.line1', { num: overDueCount })
            }}
          </div>

          <div v-if="!isSplitEnabled('fire_pod__mp_auto_pausing_update')" class="tw-mb-2">
            {{ $t('layout.companyStatus.notifyInvoicesPastDue.daysToBePaused') }}
          </div>
          <div v-else-if="isToBePaused" class="tw-mb-2">
            {{
              daysToBePaused > 1
                ? $t('layout.companyStatus.notifyInvoicesPastDue.daysToBePausedCountdownPlural', { daysToBePaused })
                : $t('layout.companyStatus.notifyInvoicesPastDue.daysToBePausedCountdown', { daysToBePaused })
            }}
          </div>

          <div v-if="permissions && permissions.canManageBilling">
            {{ $t('layout.companyStatus.notifyInvoicesPastDue.adminAction') }}
          </div>
          <div v-else>{{ $t('layout.companyStatus.notifyInvoicesPastDue.nonAdminAction') }}</div>

          <router-link
            v-if="permissions && permissions.canManageBilling"
            :to="{ name: ROUTE_NAMES.BILLING, params: { company: activeCompany.slug } }"
            @click="onClickViewBilling"
          >
            <Button inline class="block">
              {{ $t('common.viewBillingInfo') }}
            </Button>
          </router-link>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  .past-due {
    background-color: $purple-700;
    border-color: rgba($red, 0.5);

    // for the <p> in <ll-alert>
    > p {
      padding: 0 !important;
    }

    svg:global(.icon--deny) {
      display: none;
    }
  }

  .past-due-text {
    font-size: 0.875rem;
  }
</style>
